import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { User } from '../types'
import Sidebar from './Sidebar'
import TodayView from './views/TodayView'
import SourcedVehicles from './views/SourcedVehicles'
import DealsView from './views/DealsView'
import ContractsView from './views/ContractsView'
import SaleDetailsView from './views/SaleDetailsView'
import StockManagementView from './views/StockManagementView'
import ManualReviewQueue from './views/ManualReviewQueue'
import PaymentRunView from './PaymentRunView'
import PaymentApprovalView from './PaymentApprovalView'
import FeedbackView from './views/FeedbackView'
import GSTRegisterView from './views/GSTRegisterView'
import ARRemindersView from './views/ARRemindersView'
import AdminView from './views/AdminView'
import ReportsView from './views/ReportsView'
import { SmartInboxView } from './views/SmartInboxView'
import CommissionAdminView from './views/CommissionAdminView'
import CommissionReconView from './views/CommissionReconView'
import StandardCostsView from './views/StandardCostsView'
import SmsLogView from './views/SmsLogView'
import CostOfSalesView from './views/CostOfSalesView'
import SalesArView from './views/SalesArView'
import FeedbackWidget from './FeedbackWidget'
import ErrorBoundary from './ErrorBoundary'
import NoAccess from './NoAccess'
import DealDeepLink from './views/DealDeepLink'
import { hasPermission } from '../lib/permissions'
import { VIEW_LABELS, pathForView, viewForPath, dealIdFromPath } from '../routes'
import './Dashboard.css'

interface DashboardProps {
  user: User
}

export default function Dashboard({ user }: DashboardProps) {
  const location = useLocation()
  const navigate = useNavigate()

  // The view is derived from the URL rather than held in state, so Back works,
  // a refresh keeps you where you were, and any screen can be linked to.
  const currentView = viewForPath(location.pathname)
  const deepLinkedDealId = dealIdFromPath(location.pathname)

  const setCurrentView = (view: string) => navigate(pathForView(view))

  const [isSidebarOpen, setIsSidebarOpen] = useState(true)
  const [prefillVehicle, setPrefillVehicle] = useState<any>(null)

  // Normalise the bare root and any unknown path to a real one.
  useEffect(() => {
    const clean = location.pathname.replace(/\/+$/, '')
    if (clean === '' || clean === '/') navigate('/today', { replace: true })
  }, [location.pathname, navigate])

  const handleLogout = async () => {
    await supabase.auth.signOut()
    navigate('/login', { replace: true })
    window.location.reload()
  }

  /**
   * Permission-gated view. Previously a denied view silently rendered a bare
   * <TodayView /> with no `user` and no `onNavigate`, so the user landed on a
   * Today page that looked identical but where every card was dead and nothing
   * explained why. Now they get a named, actionable message instead.
   */
  const gate = (perm: string, label: string, node: JSX.Element) =>
    hasPermission(user, perm) ? node : (
      <NoAccess screen={label} permission={perm} onBack={() => setCurrentView('today')} />
    )

  const renderView = () => {
    switch (currentView) {
      case 'today':
        return <TodayView user={user} onNavigate={setCurrentView} />
      case 'sourced':
        return (
          <>
            {/* Only the cars we found on Autograb and entered by hand. The buying
                brief and the vehicle search both came off this screen on Nick's call:
                the brief was guidance nobody asked for, and the search duplicated
                Stock Management. */}
            <SourcedVehicles
              user={user}
              prefill={prefillVehicle}
              onPrefillConsumed={() => setPrefillVehicle(null)}
            />
          </>
        )
      case 'pipeline':
        return <DealsView user={user} />
      case 'contracts':
        return <ContractsView user={user} />
      case 'sale_details':
        return <SaleDetailsView user={user} />
      case 'stock':
        return <StockManagementView user={user} />
      case 'cost_of_sales':
        return <CostOfSalesView user={user} />
      case 'sales_ar':
        return <SalesArView user={user} />
      case 'dd_review':
        return gate('perm-review-dd', 'DD Review', <ManualReviewQueue />)
      case 'payment_run':
        return gate('perm-1st-approval', 'Payment Run (AP)', <PaymentRunView user={user} />)
      case 'payment_approval':
        return gate('perm-2nd-release', 'Payment Approval', <PaymentApprovalView user={user} />)
      case 'gst_register':
        return gate('perm-bas-signoff', 'Margin Scheme (GST Register)', <GSTRegisterView />)
      case 'ar_reminders':
        return gate('perm-manage-collections', 'AR Reminders', <ARRemindersView user={user} />)
      case 'smart_inbox':
        return gate('perm-view-inbox', 'Smart Inbox', <SmartInboxView />)
      case 'reports':
        return gate('perm-view-reports', 'Reports', <ReportsView user={user} />)
      case 'commission_recon':
        return gate('perm-view-reports', 'Commission Report', <CommissionReconView user={user} />)
      case 'standard_costs':
        return gate('perm-view-reports', 'Standard Costs', <StandardCostsView />)
      case 'commission_admin':
        return gate('perm-config-rates', 'Commissions', <CommissionAdminView user={user} />)
      case 'feedback':
        // Was `user.role === 'admin'`, which excluded super admins created by the
        // Admin panel (they are provisioned with role 'viewer'), so the nav item
        // appeared and then silently bounced them to Today.
        return gate('perm-user-management', 'Feedback', <FeedbackView />)
      case 'sms_log':
        return <SmsLogView />
      case 'settings':
        return <AdminView user={user} />
      default:
        return <TodayView user={user} onNavigate={setCurrentView} />
    }
  }

  return (
    <div className="dashboard">
      <Sidebar 
        currentView={currentView}
        onViewChange={setCurrentView}
        user={user}
        onLogout={handleLogout}
        isOpen={isSidebarOpen}
        onToggle={() => setIsSidebarOpen(!isSidebarOpen)}
      />
      <main className="dashboard-main">
        <div className="dashboard-header">
          <button 
            className="sidebar-toggle"
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          >
            ☰
          </button>
          <h1>DirectAuto</h1>
          <span className="dashboard-crumb">{VIEW_LABELS[currentView] || ''}</span>
        </div>
        <div className="dashboard-content">
          <ErrorBoundary key={location.pathname} label={VIEW_LABELS[currentView] || 'this'}>
            {renderView()}
          </ErrorBoundary>
        </div>
        {deepLinkedDealId && (
          <DealDeepLink
            user={user}
            dealId={deepLinkedDealId}
            onClose={() => navigate(pathForView('pipeline'))}
          />
        )}
      </main>
      <FeedbackWidget />
    </div>
  )
}
